import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for production error monitoring
 * Only runs in production environment
 */
export function initSentry() {
  if (import.meta.env.PROD) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN || '',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 0.1, // 10% of transactions
      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      
      // Environment and release info
      environment: import.meta.env.MODE,
      
      // Filter out non-critical errors
      beforeSend(event, hint) {
        const error = hint.originalException;
        const message = error?.toString() || '';
        
        // Suppress ResizeObserver errors
        if (message.includes('ResizeObserver')) {
          return null;
        }
        
        return event;
      },
    });
  }
}

/**
 * Capture an exception to Sentry
 */
export function captureException(error: any, context?: Record<string, any>) {
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else {
    console.error('[Command OS Error]', error, context);
  }
}

/**
 * Capture a message to Sentry
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  if (import.meta.env.PROD) {
    Sentry.captureMessage(message, level);
  } else {
    console.log(`[Command OS ${level}]`, message);
  }
}

/**
 * Set user context for error tracking
 */
export function setUser(user: { id: string; email?: string } | null) {
  if (import.meta.env.PROD) {
    Sentry.setUser(user ? {
      id: user.id,
      email: user.email,
    } : null);
  }
}
